// useSidebarAutoCollapse — panel-driven sidebar coordination (dev layout
// directive, 2026-07-14): a side panel opening on a desktop collection surface
// (filter panel, library preview) collapses the app sidebar to icon mode;
// closing restores it ONLY when that open collapsed it — a user's own
// collapse/expand always wins. Module-level coordinator so concurrent or
// handed-off holders (library: preview opens as filters close, same commit)
// restore exactly once, when the LAST holder releases.
import { useEffect, useRef } from 'react';

import { useOptionalSidebar } from '@/components/ui/sidebar';

const coordinator = {
  holders: 0,
  /** First holder found the sidebar expanded — restore on last release. */
  restoreOnRelease: false,
  /** Collapse committed — a later expanded render means the USER expanded. */
  collapseSeen: false,
};

export const useSidebarAutoCollapse = (active: boolean) => {
  const sidebar = useOptionalSidebar();
  const hasSidebar = !!sidebar;
  const isMobile = sidebar?.isMobile ?? false;
  const sidebarOpen = sidebar?.open ?? false;

  const openRef = useRef(sidebarOpen);
  openRef.current = sidebarOpen;
  const setOpenRef = useRef(sidebar?.setOpen);
  setOpenRef.current = sidebar?.setOpen;

  // A manual expand while held cancels the pending restore — the user's
  // sidebar intent outranks the panel's bookkeeping.
  useEffect(() => {
    if (coordinator.holders === 0) return;
    if (!sidebarOpen) {
      coordinator.collapseSeen = true;
    } else if (coordinator.collapseSeen) {
      coordinator.restoreOnRelease = false;
    }
  }, [sidebarOpen]);

  useEffect(() => {
    if (!active || !hasSidebar || isMobile) return;
    coordinator.holders += 1;
    if (coordinator.holders === 1 && openRef.current) {
      coordinator.restoreOnRelease = true;
      coordinator.collapseSeen = false;
      setOpenRef.current?.(false);
    }
    return () => {
      coordinator.holders -= 1;
      if (coordinator.holders > 0) return;
      // Cleanups run before creates within a commit, so a same-commit handoff
      // (filters release, preview acquires) briefly hits zero holders — defer
      // the restore decision past the effects pass and re-check.
      queueMicrotask(() => {
        if (coordinator.holders === 0 && coordinator.restoreOnRelease) {
          coordinator.restoreOnRelease = false;
          if (!openRef.current) setOpenRef.current?.(true);
        }
      });
    };
  }, [active, hasSidebar, isMobile]);
};
